import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent} from "react";
import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";

interface PickerSearchHeaderProps {
    query: string;
    onClose: () => void;
    /** Set → the header is a real input; unset → it echoes a query the document owns. */
    onQueryChange?: (query: string) => void;
    /** The input's keys, so arrows and Enter reach the list while the caret stays in the field. */
    onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
    /** The highlighted option's id; only meaningful where focus sits, which is the input. */
    activeDescendant?: string;
    listboxId?: string;
}

/**
 * The 46px search row. In "/" mode the query lives in the document, so this only echoes it with a
 * caret that blinks like one; where nothing else holds the query, the same frame holds a real input.
 */
export function PickerSearchHeader({
    query,
    onClose,
    onQueryChange,
    onKeyDown,
    activeDescendant,
    listboxId,
}: PickerSearchHeaderProps) {
    const {t} = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const ownsQuery = !!onQueryChange;

    /*
        Not `autoFocus`: in development StrictMode remounts the Popover's focus trap, whose cleanup
        hands focus back to the editor and whose remount then parks it on the Popover's Paper, so
        typing went into the document. This effect runs before the trap's (children first) on both
        mounts, so the trap finds focus already inside and leaves it here.
     */
    useEffect(() => {
        if (ownsQuery) inputRef.current?.focus();
    }, [ownsQuery]);

    /*
        When something outside takes focus just after opening (the editor's deferred focus after the link box
        closes), the Popover's trap parks it on its Paper and keys stop reaching the list. Any ancestor of the
        input taking focus hands it back here, on the next tick: a focus call inside a focus event is ignored.
     */
    useEffect(() => {
        if (!ownsQuery) return;

        const reclaim = (event: FocusEvent) => {
            const input = inputRef.current;
            if (!input || !(event.target instanceof Node) || event.target === input) return;
            if (event.target.contains(input)) setTimeout(() => input.focus());
        };

        document.addEventListener("focusin", reclaim);
        return () => document.removeEventListener("focusin", reclaim);
    }, [ownsQuery]);

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{px: 2, height: 46, flexShrink: 0}}
            >
                <SearchIcon fontSize="small" sx={{color: "text.secondary"}}/>

                {onQueryChange ? (
                    <InputBase
                        inputRef={inputRef}
                        value={query}
                        placeholder={t("Search variables")}
                        onChange={event => onQueryChange(event.target.value)}
                        onKeyDown={onKeyDown}
                        inputProps={{
                            role: "combobox",
                            "aria-label": t("Search variables"),
                            "aria-expanded": true,
                            "aria-autocomplete": "list",
                            "aria-controls": listboxId,
                            "aria-activedescendant": activeDescendant,
                        }}
                        sx={{flex: 1, minWidth: 0}}
                    />
                ) : (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{flex: 1, minWidth: 0}}>
                        <Typography noWrap sx={{color: query ? "text.primary" : "text.disabled"}}>
                            {query || t("Search variables")}
                        </Typography>
                        <Box
                            sx={{
                                width: "1px",
                                height: "1.15em",
                                bgcolor: "text.primary",
                                animation: "tiptap-variable-caret 1s step-end infinite",
                                "@keyframes tiptap-variable-caret": {"50%": {opacity: 0}},
                            }}
                        />
                    </Stack>
                )}

                <Typography
                    component="button"
                    variant="body2"
                    aria-label={t("Close")}
                    onMouseDown={(event: ReactMouseEvent) => event.preventDefault()}
                    onClick={onClose}
                    sx={{
                        color: "text.disabled",
                        appearance: "none",
                        border: 0,
                        p: 0,
                        bgcolor: "transparent",
                        fontFamily: "inherit",
                        cursor: "pointer",
                    }}
                >
                    esc
                </Typography>
            </Stack>

            <Divider/>
        </>
    );
}

export default PickerSearchHeader;
